// ProductForm.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import PopupComponent from '../../components/PopupComponent';
import ProductImageUpload from './ProductImageUpload';
import { createNewProduct, updateProduct, deleteProductImage } from '../../services/productService';
import { getAllCategories } from '../../services/categoryService';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import './productForm.scss';
import { styled } from 'styled-components';
import logger, { Logger } from '../../utils/logger';
import { 
  useOrganizationSettings, 
  formatCurrency, 
  getCurrencySymbol, 
  calculatePriceWithTax,
  getCurrencyName,    // ← add this
  OrganizationSettings
} from '../../utils/i18nUtils';
import Inputs from '../../components/inputs';
import { userPreferences } from '../../actions/actions';
import { uploadFiles, uploadProductImages } from '../../services/utilityService';


export const StyledFormContainer = styled.div`
  padding: 1.5rem;
  max-height: calc(90vh - 120px);
  overflow-y: auto;
  position: relative;
  padding-bottom: 80px; /* Make space for the sticky buttons */
`;

/* Add or update in ProductImageUpload.tsx */
const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #E5E7EB;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 5;
  
  &:hover {
    background: #ef4444;
    color: white;
  }
`;

const StyledSearchableSelect = styled.div`
  position: relative;
  width: 100%;

  .select-input-wrapper {
    position: relative;
    
    input {
      width: 100%;
      padding: 0.5rem 0.75rem;
      padding-right: 2.5rem; /* Increase right padding for the icon */
      border: 1px solid #E5E7EB;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      transition: all 0.2s;
      background-color: white;
      
      &:focus {
        outline: none;
        border-color: #2563EB;
        box-shadow: 0 0 0 1px #2563EB;
      }
      
      &.error {
        border-color: #DC2626;
        background-color: #FEF2F2;
      }
    }
    
    .search-icon {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: #6B7280;
      z-index: 1;
    }
  }

  .options-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #E5E7EB;
    border-radius: 0.375rem;
    margin-top: 0.25rem;
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .option-item {
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    &:hover {
      background-color: #F3F4F6;
    }
    &.selected {
      background-color: #EFF6FF;
      font-weight: 500;
    }
    &.no-results {
      color: #6B7280;
      font-style: italic;
    }
  }
`;

// Missing unit-gst-row styling
export const StyledUnitGSTRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
`;

// Replace the price calculation section with this new implementation
const StyledPriceCalculation = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    
    &:not(:last-child) {
      padding-bottom: 0.5rem;
      border-bottom: 1px dashed #E5E7EB;
    }
    
    .price-label {
      color: #6B7280;
      font-size: 0.875rem;
    }
    
    .price-value {
      font-weight: 500;
      color: #111827;
      
      &.with-gst {
        color: #2563EB;
      }
    }
  }
`;

export const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const StyledFieldsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const StyledFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;


export const StyledErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #DC2626;
  font-size: 0.75rem;

  svg {
    width: 12px;
    height: 12px;
  }
`;

/* Add this style for the image area */
export const StyledImageColumn = styled.div`
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  padding: 1rem;
  background-color: #F9FAFB;
`;

export const StyledFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background-color: white;
  border-top: 1px solid #E5E7EB;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.05);
  margin-bottom: 0; /* Remove any bottom margin */
  /* Add this to adjust the PopupComponent's internal spacing */
  & + * {
    margin-top: 0;
    padding-top: 0;
  }
`;

export const StyledButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;

  ${props => props.$variant === 'primary' ? `
    background-color: #2563EB;
    color: white;
    border: none;
    &:hover:not(:disabled) {
      background-color: #1D4ED8;
    }
  ` : `
    background-color: white;
    border: 1px solid #E5E7EB;
    &:hover:not(:disabled) {
      background-color: #F9FAFB;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UNIT_OPTIONS = [
    // Common Units
    { value: 'pc', label: 'Piece (Pc)' },
    { value: 'unit', label: 'Unit' },
    { value: 'pack', label: 'Pack' },
    { value: 'set', label: 'Set' },
    { value: 'box', label: 'Box' },
    { value: 'carton', label: 'Carton' },
    { value: 'bundle', label: 'Bundle' },
    { value: 'roll', label: 'Roll' },
    { value: 'dozen', label: 'Dozen (Doz)' },
    { value: 'bag', label: 'Bag' },
    { value: 'case', label: 'Case' },
    { value: 'pair', label: 'Pair' },
    
    // Weight Units
    { value: 'kg', label: 'Kilogram (Kg)' },
    { value: 'g', label: 'Gram (g)' },
    { value: 'mg', label: 'Milligram (mg)' },
    { value: 'ton', label: 'Ton' },
    { value: 'quintal', label: 'Quintal (Q)' },
    { value: 'pound', label: 'Pound (lb)' },
    { value: 'ounce', label: 'Ounce (oz)' },
    
    // Volume Units
    { value: 'l', label: 'Liter (L)' },
    { value: 'ml', label: 'Milliliter (mL)' },
    { value: 'gal', label: 'Gallon (gal)' },
    { value: 'bottle', label: 'Bottle' },
    { value: 'jar', label: 'Jar' },
    { value: 'can', label: 'Can' },
    { value: 'drum', label: 'Drum' },
    { value: 'tank', label: 'Tank' },
    
    // Length Units
    { value: 'm', label: 'Meter (m)' },
    { value: 'cm', label: 'Centimeter (cm)' },
    { value: 'mm', label: 'Millimeter (mm)' },
    { value: 'km', label: 'Kilometer (km)' },
    { value: 'ft', label: 'Feet (ft)' },
    { value: 'inch', label: 'Inch (in)' },
    { value: 'yard', label: 'Yard (yd)' },
    
    // Area Units
    { value: 'sqm', label: 'Square Meter (m²)' },
    { value: 'sqft', label: 'Square Feet (ft²)' },
    { value: 'sqyd', label: 'Square Yard (yd²)' },
    { value: 'acre', label: 'Acre' },
    { value: 'hectare', label: 'Hectare (ha)' },
    
    // Time-based Units
    { value: 'hour', label: 'Hour (hr)' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
    
    // Service Units
    { value: 'service', label: 'Service' },
    { value: 'job', label: 'Job' },
    { value: 'visit', label: 'Visit' },
    { value: 'session', label: 'Session' },
    
    // Digital Units
    { value: 'license', label: 'License' },
    { value: 'user', label: 'User' },
    { value: 'subscription', label: 'Subscription' }
  ];

const GST_OPTIONS = [
    { value: '0', label: 'GST 0%' },
    { value: '5', label: 'GST 5%' },
    { value: '12', label: 'GST 12%' },
    { value: '18', label: 'GST 18%' },
    { value: '28', label: 'GST 28%' }
  ];

interface Category {
  isDefault: boolean;
  _id: string;
  name: string;
  color?: string;
}

interface ProductFormData {
  name: string;
  category: string;
  description: string;
  price: string;
  unit: string;
  extraDetails: {
   
    gst: string;
  };
}

interface ProductFormProps {
  show: boolean;
  onHide: () => void;
  selectedProduct?: any;
  fetchProducts?: () => void;
}

interface SearchableSelectProps {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
}



interface SearchableSelectProps {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
}

// Fix the SearchableSelect component with proper types
const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  className = '',
  error
}) => {
  const [search, setSearch] = useState<string>('');
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement>(null);
  
  




  // Find the selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const selectedLabel = selectedOption?.label || '';
  
  // Filter options based on search
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <StyledSearchableSelect className="searchable-select" ref={selectRef}>
      <div className="select-input-wrapper">
        <input
          type="text"
          value={showOptions ? search : selectedLabel}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => {
            setShowOptions(true);
            setSearch('');
          }}
          placeholder={placeholder}
          className={`${className} ${error ? 'error' : ''}`}
        />
      </div>
      
      {showOptions && (
        <div className="options-list">
          <input
            type="text"
            placeholder="Type to search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: 'none',
              borderBottom: '1px solid #E5E7EB'
            }}
            autoFocus
          />
          
          {filteredOptions.length > 0 ? (
            filteredOptions.map(opt => (
              <div
                key={opt.value}
                className={`option-item ${value === opt.value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setShowOptions(false);
                }}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="option-item no-results">No matches found</div>
          )}
        </div>
      )}
    </StyledSearchableSelect>
  );
};

const ProductForm: React.FC<ProductFormProps> = ({
  show,
  onHide,
  selectedProduct,
  fetchProducts
}) => {
  Logger.log('ProductForm render:', { show, isEdit: Boolean(selectedProduct) });

  const { 
    currency: currencyCode, 
    locale, 
    timeZone 
  }: OrganizationSettings = useOrganizationSettings();
  const currencySymbol = getCurrencySymbol(currencyCode, locale);
  logger.log('ProductForm initialized with:', {
    currencyCode,
    currencySymbol,
    locale,
    timeZone
  });
  
  const customFieldDefs = useSelector((state: any) =>
    state?.rootReducers?.userData?.userDetails?.userPreference?.productForm || []
  );
  logger.log('👀 Custom field defs from Redux:', customFieldDefs);

  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);


const [renderKey, setRenderKey] = useState(0);

  const isEditMode = Boolean(selectedProduct);
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [errors, setErrors] = useState<Partial<ProductFormData>>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const initializationRef = useRef(false);

  const bucketUrl = useSelector((state: any) => 
    state.rootReducers?.userData?.userDetails?.bucketUrl || ''
  );

  const [visibleImages, setVisibleImages] = useState<any[]>([]);
  
  useEffect(() => {
    if (show) {
      logger.log('ProductForm opened:', { 
        isEditMode, 
        selectedProductExists: Boolean(selectedProduct),
        selectedProductId: selectedProduct?._id || 'none'
      });
    }
  }, [show, isEditMode, selectedProduct]);

  useEffect(() => {
    if (show && selectedProduct?.images) {
      setVisibleImages(selectedProduct.images);
    } else {
      setVisibleImages([]);
    }
  }, [show, selectedProduct]);

  const initialFormState = useCallback(() => {
    // Initialize custom fields from Redux
    const customFieldsInit: any = {
      gst: '0'
    };

    // Add custom fields with default values
    customFieldDefs?.forEach((field: any) => {
      if (field.type !== 'file' && field.value) {
        switch (field.type) {
          case 'multi-selection':
            customFieldsInit[field.value] = [];
            break;
          case 'number':
          case 'amount':
            customFieldsInit[field.value] = '';
            break;
          case 'boolean':
            customFieldsInit[field.value] = false;
            break;
          default:
            customFieldsInit[field.value] = '';
        }
      }
    });

    // Find default category (keep original logic)
    const defaultCategory = categories.find(cat => cat.isDefault === true);

    return {
      name: '',
      category: defaultCategory?._id || '',
      description: '',
      price: '',
      unit: '',
      extraDetails: customFieldsInit
    };
  }, [customFieldDefs, categories]);

  const [productData, setProductData] = useState<ProductFormData>({
    name: selectedProduct?.name || '',
    category: selectedProduct?.category?._id || '',
    description: selectedProduct?.description || '',
    price: selectedProduct?.price?.toString() || '',
    unit: selectedProduct?.extraDetails?.unit || selectedProduct?.unit || '', // Support both old and new structure
    extraDetails: {
      gst: selectedProduct?.extraDetails?.gst || '0'
    }
  });



// Simplified initialization useEffect
useEffect(() => {
  logger.log('🚨 [useEffect INITIALIZATION] Triggered - show:', show, 'selectedProduct:', selectedProduct?._id);

  // Reset initialization when form closes
  if (!show) {
    logger.log('🚨 [useEffect INITIALIZATION] Form closed, resetting initialization ref');
    initializationRef.current = false;
    return;
  }

  // Prevent multiple initializations
  if (initializationRef.current) {
    logger.log('🚨 [useEffect INITIALIZATION] Already initialized, skipping');
    return;
  }

  logger.log('🚨 [useEffect INITIALIZATION] Form shown, initializing with product:', selectedProduct?._id);
  logger.log("Form shown, initializing with product:", selectedProduct?._id);

  // Always reset loading state first
  setLoading(false);
  logger.log('🚨 [useEffect INITIALIZATION] Reset loading state to false');

  if (isEditMode && selectedProduct) {
    logger.log('🚨 [useEffect INITIALIZATION] EDIT MODE - setting form data for product:', selectedProduct._id);
    logger.log("Initializing form with product data:", {
      name: selectedProduct?.name,
      category: selectedProduct?.category?._id,
      price: selectedProduct?.price
    });

    // Initialize custom fields from Redux for edit mode
    const editCustomFields: any = {
      gst: selectedProduct?.extraDetails?.gst || '0'
    };

    // Add custom fields with values from selected product or defaults
    customFieldDefs?.forEach((field: any) => {
      if (field.type !== 'file' && field.value) {
        const existingValue = selectedProduct?.extraDetails?.[field.value];
        if (existingValue !== undefined) {
          editCustomFields[field.value] = existingValue;
        } else {
          // Set default values for fields not in selected product
          switch (field.type) {
            case 'multi-selection':
              editCustomFields[field.value] = [];
              break;
            case 'number':
            case 'amount':
              editCustomFields[field.value] = '';
              break;
            case 'boolean':
              editCustomFields[field.value] = false;
              break;
            default:
              editCustomFields[field.value] = '';
          }
        }
      }
    });

    // Set form data from selected product
    const editFormData = {
      name: selectedProduct?.name || '',
      category: selectedProduct?.category?._id || '',
      description: selectedProduct?.description || '',
      price: selectedProduct?.price?.toString() || '',
      unit: selectedProduct?.extraDetails?.unit || selectedProduct?.unit || '', // Support both old and new structure
      extraDetails: editCustomFields
    };
    logger.log('🚨 [useEffect INITIALIZATION] EDIT MODE - Setting productData:', editFormData);
    setProductData(editFormData);

    // Set visible images for UI display
    setVisibleImages(selectedProduct.images || []);

    // Clear any previous file selections
    setSelectedFiles([]);
    setImagesToDelete([]);

  } else if (!isEditMode) {
    // For new products, use initialFormState which includes default category
    logger.log('🚨 [useEffect INITIALIZATION] NEW PRODUCT MODE - resetting form');
    logger.log("Initializing new product form");

    const newFormData = initialFormState();
    logger.log('🚨 [useEffect INITIALIZATION] NEW PRODUCT MODE - Setting form data:', newFormData);

    setProductData(newFormData);
    setSelectedFiles([]);
    setImagesToDelete([]);
    setVisibleImages([]);
  }

  // Mark as initialized
  initializationRef.current = true;

  logger.log('🚨 [useEffect INITIALIZATION] COMPLETED');
}, [show, selectedProduct?._id, isEditMode, initialFormState, customFieldDefs]);

  // Effect to load categories
  useEffect(() => {
    const loadCategories = async () => {
      if (!show) return;
      
      setCategoryLoading(true);
      try {
        const response = await getAllCategories({
          page: 1,
          perPage: 100,
          orderBy: 'name',
          isAscending: true
        });
        if (response?.data?.data) {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        toast.error('Failed to load categories');
      } finally {
        setCategoryLoading(false);
      }
    };

    loadCategories();
  }, [show]);



const handleFilesChange = (files: File[]) => {
  logger.log(`[ProductForm] Files selected for upload: ${files.length}`);
  
  // Filter out files that appear to be duplicates
  const existingFileNames = selectedProduct?.images
    ?.map((img: any) => img.fileName?.toLowerCase())
    .filter(Boolean) || [];
  
  const newFiles = files.filter(file => {
    const isDuplicate = existingFileNames.includes(file.name.toLowerCase());
    if (isDuplicate) {
      logger.log(`[ProductForm] Skipping duplicate file: ${file.name}`);
    }
    return !isDuplicate;
  });
  
  logger.log(`[ProductForm] After filtering duplicates: ${newFiles.length} files to upload`);
  
  // Update selected files
  setSelectedFiles(prev => [...prev, ...newFiles]);
  
  // Create preview URLs for immediate display
  const newPreviews = newFiles.map(file => ({
    isPreview: true, // Mark as preview so we can style differently
    fileName: file.name,
    previewUrl: URL.createObjectURL(file)
  }));
  
  // Add these to visible images for UI
  setVisibleImages(prev => [...prev, ...newPreviews]);
};



  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setProductData(initialFormState());
        setErrors({});
        setSelectedFiles([]);
        setHasUnsavedChanges(false);
        onHide();
      }
    } else {
      onHide();
    }
  }, [hasUnsavedChanges, initialFormState, onHide]);

  const handleDeleteImage = (imageId: string) => {
    logger.log(`[ProductForm] Handling deletion for: ${imageId}`);

    // Check if this is a preview image (fileName) or existing image (filePath)
    const isPreviewImage = visibleImages.some(img => img.isPreview && img.fileName === imageId);

    if (isPreviewImage) {
      logger.log(`[ProductForm] Removing preview image: ${imageId}`);

      // Remove from visibleImages
      setVisibleImages(prev => {
        const updated = prev.filter(img => !(img.isPreview && img.fileName === imageId));

        // Also revoke the preview URL to free memory
        const previewImg = prev.find(img => img.isPreview && img.fileName === imageId);
        if (previewImg && previewImg.previewUrl) {
          URL.revokeObjectURL(previewImg.previewUrl);
        }

        return updated;
      });

      // Remove from selectedFiles
      setSelectedFiles(prev => prev.filter(file => file.name !== imageId));

    } else {
      logger.log(`[ProductForm] Marking existing image ${imageId} for deletion`);

      // Add to deletion queue
      setImagesToDelete(prev => {
        if (prev.includes(imageId)) return prev;
        return [...prev, imageId];
      });

      // Remove from visibleImages for UI feedback
      setVisibleImages(prev => prev.filter(img => img.filePath !== imageId));
    }

    setHasUnsavedChanges(true);
  };

const handleChange = useCallback((
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
) => {
  const { name, value } = e.target;
  logger.log('🔥 [handleChange] START - field:', name, 'value:', value);
  logger.log('🔥 [handleChange] Current productData before update:', productData);
  logger.log('[handleChange] field:', name, 'value:', value);

  setProductData(prev => {
    logger.log('🔥 [setProductData] Previous state:', prev);
    // 1) extraDetails.<dynamicKey>
    if (name.startsWith('extraDetails.')) {
      const key = name.split('.')[1];
      logger.log('🔥 [extraDetails] Updating key:', key, 'with value:', value);
      logger.log('[handleChange] updating extraDetails key:', key);
      const newState = {
        ...prev,
        extraDetails: {
          ...prev.extraDetails,
          [key]: value
        }
      };
      logger.log('🔥 [extraDetails] New state:', newState);
      return newState;
    }

    // 2) top‑level GST field
    if (name === 'gst') {
      logger.log('🔥 [GST] Updating GST with value:', value);
      logger.log('[handleChange] updating GST');
      const newState = {
        ...prev,
        extraDetails: {
          ...prev.extraDetails,
          gst: value
        }
      };
      logger.log('🔥 [GST] New state:', newState);
      return newState;
    }

    // 3) top‑level unit field (SearchableSelect)
    if (name === 'unit') {
      logger.log('🔥 [UNIT] Updating unit with value:', value);
      logger.log('[handleChange] updating unit');
      const newState = {
        ...prev,
        unit: value,
        extraDetails: {
          ...prev.extraDetails,

        }
      };
      logger.log('🔥 [UNIT] New state:', newState);
      return newState;
    }

    // 4) price field: ensure non‑negative number
    if (name === 'price') {
      const sanitized = value === '' ? '' : Math.max(0, Number(value)).toString();
      logger.log('🔥 [PRICE] Original value:', value, 'Sanitized value:', sanitized);
      logger.log('[handleChange] updating price to:', sanitized);
      const newState = {
        ...prev,
        price: sanitized
      };
      logger.log('🔥 [PRICE] New state:', newState);
      return newState;
    }

    // 5) everything else (name, category, description, etc.)
    logger.log('🔥 [TOP_LEVEL] Updating field:', name, 'with value:', value);
    logger.log('[handleChange] updating top‑level:', name);
    const newState = {
      ...prev,
      [name]: value
    };
    logger.log('🔥 [TOP_LEVEL] New state:', newState);
    return newState;
  });

  setHasUnsavedChanges(true);
  logger.log('🔥 [handleChange] Set unsaved changes to true');

  // Clear any existing field‑specific errors
  if (errors[name as keyof ProductFormData]) {
    logger.log('🔥 [handleChange] Clearing error for field:', name);
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }

  logger.log('🔥 [handleChange] END - completed processing for field:', name);
}, [errors]);


  const getUnitLabel = useCallback((unitValue: string) => {
    const unit = UNIT_OPTIONS.find(opt => opt.value === unitValue);
    return unit ? unit.label.split(' ')[0].toLowerCase() : unitValue;
  }, []);

  const calculateGSTPrice = useCallback((price: string | number, gst: string | number): string => {
    if (!price || isNaN(Number(price))) return '';
    const totalPrice = calculatePriceWithTax(price, gst);
    return formatCurrency(totalPrice, currencyCode, locale);
  }, [currencyCode, locale]);
  
  


  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<ProductFormData> = {};

    if (!productData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    if (!productData.category) {
      newErrors.category = 'Please select a category';
    }
    if (!productData.price || Number(productData.price) <= 0) {
      newErrors.price = 'Please enter a valid price';
    }
    if (!productData.unit) {
      newErrors.unit = 'Please select a unit type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [productData]);



// Ensure complete form reset when closing
const handleFormClose = useCallback(() => {
  logger.log("Preparing to close form - resetting all states");
  
  // Reset all states
  setLoading(false);
  setImagesToDelete([]);
  setSelectedFiles([]);
  setVisibleImages([]);
  setHasUnsavedChanges(false);
  setErrors({});
  setProductData(initialFormState());
  
  // Then call the original onHide
  onHide();
}, [onHide, initialFormState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.log('Form submitted - starting validation');
    
    if (!validateForm()) {
      logger.log('Validation failed');
      toast.error('Please fill all required fields correctly');
      return;
    }
  
    setLoading(true);
    logger.log('Validation passed - proceeding with', isEditMode ? 'update' : 'create');
    
    try {
      if (isEditMode) {
        logger.log('Edit mode: Updating product with ID:', selectedProduct._id);
        
        // 1. Process any image deletions first
        if (imagesToDelete.length > 0) {
          logger.log(`Processing ${imagesToDelete.length} image deletion(s):`, imagesToDelete);
          
          // Create a Set to remove any duplicate image IDs
          const uniqueImageIds = [...new Set(imagesToDelete)];
          logger.log(`After removing duplicates: ${uniqueImageIds.length} unique images to delete`);
          
          for (const imageId of uniqueImageIds) {
            try {
              logger.log(`Attempting to delete image: ${imageId}`);
              await deleteProductImage(selectedProduct._id, imageId);
              logger.log(`Successfully deleted image: ${imageId}`);
            } catch (deleteError) {
              console.error(`Failed to delete image ${imageId}:`, deleteError);
              // Continue with other deletions even if one fails
            }
          }
        } else {
          logger.log('No images to delete');
        }
        
        // 2. Update product data
        const updateData = {
          name: productData.name,
          category: productData.category,
          price: productData.price,
          currency: currencyCode,
          currencyName: getCurrencyName(currencyCode, locale),
          ...(productData.description ? { description: productData.description } : {}),
          unit: productData.unit,
          gst: productData.extraDetails.gst,
          extraDetails: {
            ...productData.extraDetails, // Include all custom fields
            unit: productData.unit,
            gst: productData.extraDetails.gst
          }
        };
        
        logger.log('Sending product update data:', updateData);
  const response = await updateProduct(selectedProduct._id, updateData);
  logger.log('Product update response:', response.data);
  
  // 3. Upload new images if any
  if (selectedFiles.length > 0) {
    logger.log(`Uploading ${selectedFiles.length} new image(s) for existing product`);
    try {
      const uploadResponse = await uploadProductImages(selectedFiles, selectedProduct._id);
      logger.log('Image upload response:', uploadResponse);
      
      // Add this code right here, after the successful upload
      setImagesToDelete([]);
      setSelectedFiles([]);
      
    } catch (uploadError) {
      console.error('Error uploading images:', uploadError);
      // Continue with form submission even if image upload fails
    }
  } else {
    logger.log('No new images to upload for this product');
  }
        
        logger.log('Product update completed successfully');
        toast.success('Product updated successfully');
        setHasUnsavedChanges(false);
        fetchProducts?.();
        onHide();
        
      } else {
        logger.log('Create mode: Creating new product');
        
        // 1. Create product first to get ID
        const createData = {
          name: productData.name,
          category: productData.category,
          price: productData.price,
          currency: currencyCode,
          currencyName: getCurrencyName(currencyCode, locale),
          ...(productData.description ? { description: productData.description } : {}),
          unit: productData.unit,
          gst: productData.extraDetails.gst,
          extraDetails: {
            ...productData.extraDetails, // Include all custom fields
            unit: productData.unit,
            gst: productData.extraDetails.gst
          }
        };
        
        logger.log('Sending product creation data:', createData);
        const response = await createNewProduct(createData);
        logger.log('Product creation response:', response.data);
        
        // 2. Get the new product ID from response
        const newProductId = response.data.data._id;
        logger.log('New product created with ID:', newProductId);
        
        // 3. Upload images with that product ID if any
        if (selectedFiles.length > 0 && newProductId) {
          logger.log(`Uploading ${selectedFiles.length} image(s) for new product`);
          try {
            const uploadResponse = await uploadProductImages(selectedFiles, newProductId);
            logger.log('Image upload response:', uploadResponse);
          } catch (uploadError) {
            console.error('Error uploading images:', uploadError);
            // Continue with success message even if image upload fails
          }
        } else {
          logger.log('No images to upload for this new product', {
            hasFiles: selectedFiles.length > 0,
            hasProductId: Boolean(newProductId)
          });
        }
        
        logger.log('Product creation completed successfully');
        toast.success('Product created successfully');
        setHasUnsavedChanges(false);
        fetchProducts?.();
        onHide();
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }  finally {
      // IMPORTANT: Always reset loading state in finally block
      setLoading(false);
    }
  };


  return (
    <PopupComponent
    isOpen={show}
    onClose={handleFormClose} // Use the new wrapper instead of direct onHide
    title={isEditMode ? 'Edit Product' : 'Add New Product'}
    size="lg"
  >
      <StyledFormContainer>
        <form onSubmit={handleSubmit}>
        <StyledFormGrid>
  <StyledImageColumn>
  <ProductImageUpload

                existingImages={visibleImages}
                onFilesChange={handleFilesChange}
                onDeleteImage={handleDeleteImage}
                bucketUrl={bucketUrl}
                showPreview={true} // Add a flag to indicate previews should be shown

              />
  </StyledImageColumn>



            <StyledFieldsColumn>
              <StyledFieldRow>
                <Inputs
                  name="name"
                  type="text"
                  label="Product Name"
                  value={productData.name}
                  placeholder="Enter product name"
                  isRequired={true}
                  options={[]}
                  onChange={handleChange}
                  className="form-control"
                  autoComplete="off"
                  id="name"
                />
                {errors.name && (
                  <StyledErrorMessage>
                    <AlertCircle />
                    <span>{errors.name}</span>
                  </StyledErrorMessage>
                )}
              </StyledFieldRow>
  
              <StyledFieldRow>
                <Inputs
                  name="category"
                  type="selection"
                  label="Category"
                  value={categories.find(cat => cat._id === productData.category)?.name || ''}
                  placeholder="Select category"
                  isRequired={true}
                  options={categories.map(cat => cat.name)}
                  onChange={(e) => {
                    const selectedCategory = categories.find(cat => cat.name === e.target.value);
                    handleChange({ target: { name: 'category', value: selectedCategory?._id || '' } } as any);
                  }}
                  className="form-control"
                  autoComplete="off"
                  id="category"
                />
                {errors.category && (
                  <StyledErrorMessage>
                    <AlertCircle />
                    <span>{errors.category}</span>
                  </StyledErrorMessage>
                )}
              </StyledFieldRow>
  
              <StyledFieldRow>
                <Inputs
                  name="price"
                  type="currency"
                  label="Price"
                  value={productData.price}
                  placeholder=""
                  isRequired={true}
                  options={[]}
                  onChange={handleChange}
                  className="form-control"
                  autoComplete="off"
                  id="price"
                  currencySymbol={currencySymbol}
                  unitLabel={getUnitLabel(productData.unit)}
                />
                {errors.price && (
                  <StyledErrorMessage>
                    <AlertCircle />
                    <span>{errors.price}</span>
                  </StyledErrorMessage>
                )}
              </StyledFieldRow>
              <StyledUnitGSTRow>
  <div className={errors.unit ? 'has-error' : ''}>
  <SearchableSelect
  options={UNIT_OPTIONS}
  value={productData.unit}
  onChange={(value) => handleChange({ target: { name: 'unit', value }} as any)}
  placeholder="Search unit type" // No extra spaces needed
  className="field-select"
  error={Boolean(errors.unit)}
/>
    {errors.unit && (
      <StyledErrorMessage>
        <AlertCircle size={12} />
        <span>{errors.unit}</span>
      </StyledErrorMessage>
    )}
  </div>
  <div>
    <Inputs
      name="gst"
      type="selection"
      label="GST"
      value={GST_OPTIONS.find(opt => opt.value === productData.extraDetails.gst)?.label || ''}
      placeholder="Select GST"
      isRequired={false}
      options={GST_OPTIONS.map(opt => opt.label)}
      onChange={(e) => {
        const selectedGST = GST_OPTIONS.find(opt => opt.label === e.target.value);
        handleChange({ target: { name: 'gst', value: selectedGST?.value || '' } } as any);
      }}
      className="form-control"
      autoComplete="off"
      id="gst"
    />
  </div>
</StyledUnitGSTRow>



{/* --- Dynamic extraDetails fields (beyond unit & gst) --- */}
{Object.entries(productData.extraDetails)
  .filter(([key]) => !['unit', 'gst'].includes(key))
  .map(([key, val]) => {
    const def = customFieldDefs.find((f: { value?: string; name?: string; type?: string; label?: string; options?: string[] }) => 
      (f.value === key) || (f.name === key)
    ) || {};
    
    // If there's no definition, still render as a fallback
    const type = def.type ?? (Array.isArray(val) ? 'multi-selection' : 'text');
    const label = def.label ?? key;
    const options = def.options ?? [];

    // Skip file fields
    if (type === 'file') {
      return null;
    }

    return (
      <StyledFieldRow key={key}>
        <Inputs
          name={`extraDetails.${key}`}
          type={type}
          label={label}
          value={val as any}
          placeholder={def.placeholder || ''}
          isRequired={def.isRequired || false}
          options={options}
          onChange={handleChange}
          className="form-control"
          autoComplete="off"
          id={key}
        />
      </StyledFieldRow>
    );
  })}


            {/* Description field */}
              <StyledFieldRow>
                <Inputs
                  name="description"
                  type="text"
                  label="Description"
                  value={productData.description}
                  placeholder="Enter product description"
                  isRequired={false}
                  options={[]}
                  onChange={handleChange}
                  className="form-control"
                  autoComplete="off"
                  id="description"
                />
              </StyledFieldRow>
            </StyledFieldsColumn>
          </StyledFormGrid>
  
          <StyledFormActions>
  <StyledButton 
    type="button" 
    $variant="secondary"
    onClick={onHide}
    disabled={loading}
  >
    Cancel
  </StyledButton>
  <StyledButton
    type="submit"
    $variant="primary"
    disabled={loading}
  >
    {loading ? 
      (isEditMode ? "Updating..." : "Creating...") : 
      (isEditMode ? "Update Product" : "Create Product")
    }
  </StyledButton>
</StyledFormActions>
        </form>
      </StyledFormContainer>
    </PopupComponent>
  );
};

export default ProductForm;